import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {FirmaEntitiesActions} from './firma-entities.actions';
import {of, switchMap, tap} from 'rxjs';
import {DatevIncometaxExportConfigService, FirmaDTO} from '../../openapi/export-openapi';
import {NGXLogger} from 'ngx-logger';
import {catchError} from 'rxjs/operators';
import {MappedHttpErrorResponse, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {ActionCreator} from '@ngrx/store';
import {TypedAction} from '@ngrx/store/src/models';


export const FIRMA_ENTITIES_LOCAL_STORAGE_KEY = 'firmenUsedInThePast';

@Injectable()
export class FirmaEntitiesEffects {

  private readonly _logger = inject(NGXLogger);

  private readonly _actions$ = inject(Actions);

  private readonly _exportService = inject(DatevIncometaxExportConfigService);

  /**
   * Der Effekt erlaubt asynchrones Laden von Fakturierungsbelegen durch serverseitige Datenanfragen.
   * Dabei steuert er die Auslösung von Actions basierend auf dem Erfolg oder Misserfolg der Anfrage.
   *
   * @property onAction - Action Creator, der ausgeführt wird, um den Effekt zu starten
   * @property successAction - Action Creator, der ausgeführt wird, wenn die Daten erfolgreich vom Server abgerufen wurden
   * @property failureAction - Action Creator, der ausgeführt wird, wenn beim Abrufen der Daten ein Fehler aufgetreten ist
   */
  readonly getFirma$ = (
    onAction: ActionCreator<any, (props: {
      firmenId: string,
    }) => ({
      firmenId: string,
    } & TypedAction<any>)>,
    successAction?: ActionCreator<any, (props: { firmaDto: FirmaDTO }) => ({
      firmaDto: FirmaDTO,
    } & TypedAction<any>)>,
    failureAction?: ActionCreator<any, (props: { firmenId: string, error: MappedHttpErrorResponse }) => ({
      firmenId: string,
      error: MappedHttpErrorResponse,
    } & TypedAction<any>)>,
  ) => createEffect(
    () => this._actions$.pipe(
      ofType(onAction),
      switchMap(({firmenId}) => {
        return this._exportService.getFirma(
          firmenId,
        ).pipe(
          switchMap(firmaDto => {
            return [
              FirmaEntitiesActions.getFirmaSuccess({firmaDto}),
              ...(successAction ? [successAction({firmaDto})] : []),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => {
              return [
                FirmaEntitiesActions.getFirmaFailure({firmenId, error}),
                ...(failureAction ? [failureAction({firmenId, error})] : []),
              ];
            }),
          )),
        );
      }),
    )
  );

  readonly getFirmaSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(FirmaEntitiesActions.getFirmaSuccess),
      tap(({firmaDto}) => {
        this._logger.debug('Firma retrieved successfully:', firmaDto);
        let firmenUsedInThePastLocalStorage = localStorage.getItem(FIRMA_ENTITIES_LOCAL_STORAGE_KEY);
        const firmenUsedInThePast: FirmaDTO[] = firmenUsedInThePastLocalStorage ? JSON.parse(firmenUsedInThePastLocalStorage) : [];
        if (!firmenUsedInThePast.some(f => f.id === firmaDto.id)) {
          firmenUsedInThePast.push(firmaDto);
          localStorage.setItem(FIRMA_ENTITIES_LOCAL_STORAGE_KEY, JSON.stringify(firmenUsedInThePast));
        }
      })
    ),
    {dispatch: false}
  );

  readonly getFirmaFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(FirmaEntitiesActions.getFirmaFailure),
      tap(({error, firmenId}) => {
        this._logger.error('Error retrieving firma:', firmenId, error);
        let firmenUsedInThePastLocalStorage = localStorage.getItem(FIRMA_ENTITIES_LOCAL_STORAGE_KEY);
        const firmenUsedInThePast: FirmaDTO[] = firmenUsedInThePastLocalStorage ? JSON.parse(firmenUsedInThePastLocalStorage) : [];
        if (firmenUsedInThePast.some(f => f.id === firmenId)) {
          const updatedFirmen = firmenUsedInThePast.filter(f => f.id !== firmenId);
          localStorage.setItem(FIRMA_ENTITIES_LOCAL_STORAGE_KEY, JSON.stringify(updatedFirmen));
        }
      })
    ),
    {dispatch: false}
  );

}
